import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Overline, MaskLine, EASE } from "@/components/Motion";
import { GALLERY_FULL, GALLERY_FILTERS } from "@/data/content";

export default function GalleryPage() {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const items = filter === "all" ? GALLERY_FULL : GALLERY_FULL.filter((g) => g.cat === filter);

  return (
    <main className="pt-[72px]" data-testid="gallery-page">
      <section className="bg-wine text-cream py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Overline className="text-brass">Moments & Plates</Overline>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 max-w-4xl" data-testid="gallery-page-title">
            <MaskLine>Inside</MaskLine>
            <MaskLine delay={0.15}>
              <span className="italic text-brass">Forco Amore</span>
            </MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
            className="mt-6 max-w-xl text-cream/80 text-base sm:text-lg font-light leading-relaxed"
          >
            Our plates, our space and the events we cater — captured between the lunch rush and the
            last espresso.
          </motion.p>
        </div>
      </section>

      <div className="sticky top-[72px] z-[60] border-b border-sand bg-cream/90 backdrop-blur-md">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex gap-2 overflow-x-auto py-4 scrollbar-none" data-testid="gallery-filter-tabs">
            {GALLERY_FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`gallery-filter-${f.id}`}
                className={`whitespace-nowrap rounded-full px-6 py-2.5 text-[12px] tracking-[0.14em] uppercase transition-all duration-300 ${
                  filter === f.id
                    ? "bg-wine text-cream shadow-[0_10px_25px_-10px_rgba(114,47,55,0.5)]"
                    : "border border-sand text-faded hover:border-wine/40 hover:text-wine"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-6">
            <AnimatePresence mode="popLayout">
              {items.map((item, i) => (
                <motion.div
                  key={item.img}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.05, ease: EASE }}
                  className="mb-6 break-inside-avoid"
                >
                  <button
                    onClick={() => setActive(item)}
                    className="group relative block w-full overflow-hidden rounded-[1.5rem] text-left"
                    data-testid={`gallery-page-item-${i}`}
                    aria-label={`Open photo: ${item.label}`}
                  >
                    <img
                      src={item.img}
                      alt={item.label}
                      loading="lazy"
                      className={`img-lux w-full ${item.aspect} object-cover`}
                    />
                    <div className="absolute inset-0 bg-wine/0 group-hover:bg-wine/25 transition-colors duration-500" />
                    <span className="absolute bottom-5 left-5 text-cream text-xs tracking-[0.25em] uppercase opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                      {item.label}
                    </span>
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-[110] bg-ink/92 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setActive(null)}
            data-testid="gallery-page-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-cream/80 hover:text-cream p-2"
              onClick={() => setActive(null)}
              aria-label="Close photo"
              data-testid="gallery-page-lightbox-close"
            >
              <X size={30} strokeWidth={1.2} />
            </button>
            <motion.figure
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={active.img}
                alt={active.label}
                className="w-full max-h-[80vh] object-contain rounded-2xl"
                data-testid="gallery-page-lightbox-image"
              />
              <figcaption className="mt-4 text-center text-cream/70 text-xs tracking-[0.3em] uppercase">
                {active.label}
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
